import type { SortDirection, TournamentSort } from '@brush/api-client';

/**
 * The table's order. Deliberately separate from `TournamentFilter`: sorting changes the order
 * of the matched set, never its membership, so it is neither summarised as a filter chip nor
 * kept in the operator's saved starting point.
 *
 * The server does the ordering — sorting only the rows on the current page would silently
 * reorder 25 of N tournaments and present it as the order of the whole set.
 */
export interface TournamentSortState {
  column: TournamentSort;
  direction: SortDirection;
}

/**
 * Earliest first — the list read as a schedule, which is what a room uses it for. A results
 * archive wants the newest at the top; a running order wants the next thing to happen.
 */
export const DEFAULT_SORT: TournamentSortState = { column: 'date', direction: 'asc' };

/**
 * Where a column starts when you first click it. Money and entries are wanted biggest-first, a
 * name A→Z, and a date in the order the events actually run — the same direction the list opens
 * in, so returning to it from another column returns to what you started with.
 */
const FIRST_DIRECTION: Record<TournamentSort, SortDirection> = {
  date: 'asc',
  name: 'asc',
  buyIn: 'desc',
  entries: 'desc',
};

/** Clicking the active column reverses it; clicking another opens it at its natural end. */
export function nextSort(
  current: TournamentSortState,
  column: TournamentSort,
): TournamentSortState {
  return current.column === column
    ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { column, direction: FIRST_DIRECTION[column] };
}
